import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { CheckCircle, Warning, FileText, ArrowsClockwise } from "@phosphor-icons/react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function IssuingTermsAccept() {
  const { token } = useParams();
  const t = useT();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [signature, setSignature] = useState("");
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const loadInfo = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/public/issuing/accept-terms/${token}`);
      setInfo(r.data);
      if (r.data.receiver_name) setSignature(r.data.receiver_name);
    } catch (err) {
      setLoadError(err.response?.data?.detail || t("terms.load_error_default"));
    }
  }, [token, t]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!checked || !signature.trim()) {
      toast.error(t("terms.toast.sign_and_check"));
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API_BASE}/api/public/issuing/accept-terms/${token}`, {
        full_name: signature.trim(),
      });
      setDone(r.data);
      toast.success(r.data.message || t("terms.toast.recorded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("terms.toast.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-rose-200 p-6 text-center" data-testid="terms-error">
          <Warning size={42} weight="duotone" className="mx-auto text-rose-500" />
          <h1 className="serif text-2xl text-[#112318] mt-3">{t("terms.error.title")}</h1>
          <p className="text-sm text-[#4A5C50] mt-2">{loadError}</p>
          <Link to="/" className="inline-block mt-4 text-sm text-[#184A30] underline">{t("terms.back_home")}</Link>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center"><p className="text-[#4A5C50] text-sm">{t("terms.loading")}</p></div>;
  }

  if (done) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-emerald-200 p-7 text-center shadow-sm" data-testid="terms-done">
          <span className="inline-flex w-14 h-14 rounded-full bg-emerald-100 items-center justify-center">
            <CheckCircle size={32} weight="fill" className="text-emerald-600" />
          </span>
          <h1 className="serif text-3xl text-[#112318] mt-4">{t("terms.done.title")}</h1>
          <p className="text-sm text-[#4A5C50] mt-3 leading-relaxed">{done.message}</p>
          <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-left text-xs text-[#4A5C50]">
            {t("terms.done.meta", { v: done.agreement_version, ts: new Date(done.accepted_at).toLocaleString() })}
          </div>
          <p className="text-xs text-[#4A5C50] mt-5 leading-relaxed">{t("terms.done.next")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F6] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm overflow-hidden">
          <div className="px-6 pt-6 pb-5 border-b border-[#E5E0D8]">
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">
              <FileText size={12} /> {t("terms.eyebrow", { v: info.agreement_version })}
            </span>
            <h1 className="serif text-2xl text-[#112318] mt-2" data-testid="terms-heading">
              {info.already_accepted ? t("terms.title_reaccept") : t("terms.title_accept")}
            </h1>
            <p className="text-sm text-[#4A5C50] mt-2 leading-relaxed">
              {info.sender_first_name
                ? <><b>{info.sender_first_name}</b> {t("terms.desc.sender_sent")} </>
                : <>{t("terms.desc.you_were_sent")} </>}
              {info.last4 ? <>{t("terms.desc.ending")} <span className="mono font-semibold">{info.last4}</span></> : null}.
              {" "}{t("terms.desc.read_before")}
            </p>
          </div>

          {info.already_accepted && (
            <div className="px-6 pt-4 -mb-2">
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-amber-900 text-xs">
                {t("terms.already_accepted_note")}
              </div>
            </div>
          )}

          <div className="px-6 py-5">
            <div
              className="max-h-[420px] overflow-y-auto rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] p-4 text-sm text-[#112318] leading-relaxed whitespace-pre-wrap mono"
              data-testid="terms-body"
            >
              {info.agreement_markdown}
            </div>
            <p className="text-[11px] text-[#4A5C50] mt-2">
              {t("terms.scroll_hint")}{" "}
              <a href={`${API_BASE}/api/public/legal/cardholder-agreement`} target="_blank" rel="noreferrer" className="text-[#184A30] underline">
                {t("terms.view_json")}
              </a>.
            </p>
          </div>

          <form onSubmit={submit} className="px-6 pb-6 space-y-4" data-testid="terms-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#4A5C50] block mb-1">
                {t("terms.sign_label")}
              </label>
              <input
                value={signature}
                onChange={(e) => setSignature(e.target.value.slice(0, 120))}
                placeholder={t("terms.sign_placeholder")}
                className="w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-[#184A30]"
                data-testid="terms-signature-input"
                required
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer text-sm text-[#112318]">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-1"
                data-testid="terms-confirm-checkbox"
              />
              <span>{t("terms.consent", { v: info.agreement_version })}</span>
            </label>

            <button
              type="submit"
              disabled={submitting || !checked || !signature.trim()}
              className="w-full rounded-xl bg-[#184A30] hover:bg-[#0F3320] disabled:bg-[#184A30]/50 text-white font-semibold py-3 inline-flex items-center justify-center gap-2"
              data-testid="terms-accept-btn"
            >
              {submitting ? <ArrowsClockwise size={18} className="animate-spin" /> : <CheckCircle size={18} weight="bold" />}
              {submitting ? t("terms.btn_recording") : t("terms.btn_accept")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
